import { useEffect, useCallback } from 'react';
import { ChevronRight, X, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTrainingModeStore, TUTORIAL_STEPS, TUTORIAL_STEP_COUNT } from '@/store/trainingMode';

/**
 * Tutorial overlay that displays step-by-step prompts to guide the user
 * through the POS selling process while in Training Mode.
 *
 * Each step highlights a relevant area and shows instructions.
 * The tutorial can be advanced, skipped, or dismissed entirely.
 */
export function TrainingModeTutorial() {
  const {
    isTrainingMode,
    isTutorialEnabled,
    tutorialStep,
    advanceTutorial,
    toggleTutorial,
    setTutorialStep,
  } = useTrainingModeStore();

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isTutorialEnabled) return;
      if (e.key === 'Escape') {
        toggleTutorial();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        advanceTutorial();
      } else if (e.key === 'ArrowLeft' && tutorialStep > 0) {
        setTutorialStep(tutorialStep - 1);
      }
    },
    [isTutorialEnabled, tutorialStep, advanceTutorial, toggleTutorial, setTutorialStep]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isTrainingMode || !isTutorialEnabled) return null;

  const currentStep = TUTORIAL_STEPS[tutorialStep];
  if (!currentStep) return null;

  const isLastStep = tutorialStep === TUTORIAL_STEP_COUNT - 1;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Semi-transparent overlay */}
      <div className="absolute inset-0 bg-black/20 pointer-events-auto" onClick={toggleTutorial} />

      {/* Tutorial card - positioned based on step target */}
      <div
        className="pointer-events-auto absolute"
        style={getPositionForStep(tutorialStep)}
      >
        <div className="w-80 rounded-xl border bg-card shadow-xl p-4 space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/20">
                <GraduationCap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  Step {tutorialStep + 1} of {TUTORIAL_STEP_COUNT}
                </p>
                <p className="text-sm font-semibold">{currentStep.title}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={toggleTutorial}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed">
            {currentStep.description}
          </p>

          {/* Progress bar */}
          <div className="flex gap-1">
            {Array.from({ length: TUTORIAL_STEP_COUNT }).map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= tutorialStep
                    ? 'bg-amber-500'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={toggleTutorial}
            >
              Skip tutorial
            </Button>
            <Button
              size="sm"
              className="gap-1 bg-amber-500 hover:bg-amber-600 text-white"
              onClick={advanceTutorial}
            >
              {isLastStep ? 'Done' : 'Next'}
              {!isLastStep && <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Returns absolute positioning CSS for the tutorial card based on the current step.
 * Steps target different areas of the POS:
 *   0: product-search   -> top-left area
 *   1: order-panel      -> top-right area
 *   2: order-totals     -> middle-right area
 *   3: checkout-button  -> bottom-right area
 *   4: payment-actions  -> center (checkout modal)
 */
function getPositionForStep(step: number): React.CSSProperties {
  switch (step) {
    case 0: // product search - top left
      return { top: '140px', left: '40px' };
    case 1: // order panel - top right
      return { top: '140px', right: '420px' };
    case 2: // order totals - middle right
      return { top: '50%', right: '420px', transform: 'translateY(-50%)' };
    case 3: // checkout button - bottom right
      return { bottom: '160px', right: '420px' };
    case 4: // payment actions - center
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    default:
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
}
